import { TIMEOUT_ACTION } from '../constants/timeout'
import type { TimeoutRow, TimeoutTimelineEvent } from '../types/timeout'
import { toDisplayClock, toTimeoutClock } from './timeoutClock'

/**
 * A timeout as a timeline event. It is a team's event: it carries the team,
 * the period and the clock, and no player.
 */
export const toTimeoutTimelineEvent = (
  row: TimeoutRow
): TimeoutTimelineEvent => ({
  id: row.id,
  action: TIMEOUT_ACTION,
  teamId: row.team_id,
  periodId: row.period_id,
  gameTime: toTimeoutClock(row.period_clock_time) || '',
  time: toDisplayClock(row.period_clock_time),
  occurredAt: row.occurred_at,
})

export const isTimeoutEvent = (
  event?: { action?: string | null } | null
): boolean => event?.action === TIMEOUT_ACTION

interface TimelinePeriod<TEvent> {
  events?: ReadonlyArray<TEvent> | null
}

/**
 * Adds the game's timeouts to the timeline's periods, each to its own.
 *
 * A timeout whose period the game does not hold is left out. The timeline's
 * other events assume their period is there; a timeout must never be the event
 * that takes the whole timeline down.
 */
export const addTimeoutEvents = <TEvent>(
  periodsById: Record<string, { events: Array<TEvent | TimeoutTimelineEvent> }>,
  timeouts?: ReadonlyArray<TimeoutRow> | null
): void => {
  const recorded = timeouts || []
  recorded.forEach((timeout) => {
    const period = timeout ? periodsById[timeout.period_id] : undefined
    if (period) {
      period.events.push(toTimeoutTimelineEvent(timeout))
    }
  })
}

/**
 * The timeline's periods with the timeouts taken out of their events.
 *
 * For the game timeline of a basic-stats game, which shows points and a
 * per-period summary: there a period's timeouts are counted in the summary's
 * Timeouts row (its `homeTimeoutCount` and `visitingTimeoutCount`, which stay
 * as they are), never drawn as cards among the points, so a period holding
 * only a timeout shows no point card.
 */
export const withoutTimeoutEvents = <
  TEvent extends { action?: string | null },
  TPeriod extends TimelinePeriod<TEvent>
>(
  periods: ReadonlyArray<TPeriod>
): TPeriod[] =>
  periods.map((period) => ({
    ...period,
    events: (period.events || []).filter((event) => !isTimeoutEvent(event)),
  }))

interface PeriodTimeoutCounts {
  homeTimeoutCount?: number | null
  visitingTimeoutCount?: number | null
}

/**
 * Whether either team called a timeout in the period, read off the counts the
 * game timeline gives each period. A period the WebSocket added before the game
 * was read again carries no counts, and holds none.
 */
export const periodHasTimeouts = (
  period?: PeriodTimeoutCounts | null
): boolean =>
  (period?.homeTimeoutCount || 0) + (period?.visitingTimeoutCount || 0) > 0
